import ClvrComponent from '../../../../system/vue/Core/ClvrComponent'
import { Component, Prop, ProvideReactive, Watch } from 'vue-property-decorator'
import ClvrScreenControlHeader from './ClvrScreenControlHeader/ClvrScreenControlHeader.vue'
import ClvrScreenControlTable from './ClvrScreenControlTable/ClvrScreenControlTable.vue'
import { ClvrScreenControlHeadersInterface } from './interface'
import { useScreenControlStore } from '../../Store/ClvrScreenControlStore'
import { JsonApiResponseAdapter, Nullable, Undefinable } from '@internal-libraries/kheops-ui-lib'
import ClvrScreenControl from '../../Classes/ClvrScreenControl'
import { ScreenControlTemplateData } from '../../Types/ScreenControlTemplateData'
import { ClvrScreenControlHeadersConfig, ClvrScreenControlTrSizeByCategory } from './enum'
import Operation from '../../../../dPplanningOp/vue/Classes/ClvrOperation'
import {
    ClvrScreenControlHeadersEnumRefLabels,
    mappingColumn,
    HEADER_SIZE,
    PAGE_A4_HEIGHT,
    PRINT_MARGIN,
    SC_DEFAULT_HEIGHT_ROW,
    SC_DEFAULT_REFRESH_INTERVAL,
    SC_DEFAULT_ROW_SPACING,
    SC_DEFAULT_BORDER_ROW,
    PAGE_PRINT_HEIGHT,
} from './const'
import ScreenControlFakeData from '../../Classes/ScreenControlFakeData'
import { ScreenControlDataTypeData } from '../../Types/ScreenControlDataTypeData'
import ClvrScreenControlDataTypeDataHandler from '../../Classes/ClvrScreenControlDataTypeDataHandler'
import { parse } from 'date-fns'
import { CELL_COMPONENTS_MAPPING } from './ClvrScreenControlRowCell'
import { DEFAULT_ALIAS_OPTION, ScreenControlAliasOptionEnum } from '../../enum/ScreenControlAliasOption'
import { EMPTY, Observable } from 'rxjs'

type rooms = Operation[]

@Component({
    methods: {
        PRINT_MARGIN() {
            return PRINT_MARGIN
        },
    },
    components: {
        ClvrScreenControlTable,
        ClvrScreenControlHeader,
    },
})
export default class ClvrScreenControlPlanningTable extends ClvrComponent {
    @Prop({ type: Number }) private screenControlId?: number
    @Prop({ type: String }) private date?: string
    @Prop({ type: Boolean, default: false }) private modeSimulation!: boolean
    @Prop({ type: Object }) private fakeTemplate?: ScreenControlTemplateData
    @Prop({ type: Boolean, default: false }) public noHeader!: boolean
    @Prop({ type: Boolean, default: false }) public noRoomCol!: boolean
    @Prop({ type: Boolean, default: false }) public printMode!: boolean

    @ProvideReactive('template')
    public template: Nullable<ScreenControlTemplateData> = null

    @ProvideReactive('printMode')
    public printModeProvide = false

    public loadingScTemplates = false
    public dynamicRefresh = false
    public screenControlStore = useScreenControlStore()
    public screenControl: Nullable<ClvrScreenControl> = null
    public headers: ClvrScreenControlHeadersInterface[] = []
    // represents the height of a tr column depending on the screen size
    public sizeByWindowCategory: ClvrScreenControlTrSizeByCategory = ClvrScreenControlTrSizeByCategory.SMALL
    public maxDataRowCanDisplayedOnTable = 0
    // here's how it works we start on page 1, we call the database and we look at the number of data returned
    // if it is equal to maxDataRowCanDisplayedOnTable (which represents the number of data to display on the screen)
    // we increment the page +1 but if the number of data returned (lower) is not equal
    // to maxDataRowCanDisplayedOnTable then this will mean that we arrive at the end of our data and that there is no more and that there is have to start again on page 1
    private readonly FIRST_PAGE = 0
    private readonly DATE_PARSE_FORMAT = 'dd.MM.yyyy'
    private readonly WIDE_SCREEN_BREAKPOINT = 2500

    public page = this.FIRST_PAGE
    public groupOperations: Operation[][] = []
    public groupOperationsPrint: Operation[][][] = []
    private roomsId: Nullable<string> = null
    private dataTypes: ScreenControlDataTypeData[] = []
    private operations: Operation[] = []
    private refreshInterval = SC_DEFAULT_REFRESH_INTERVAL * 1000

    public hideData = false
    public totalOperation: Nullable<number> = null

    public resizeObserver: Nullable<ResizeObserver> = null

    protected beforeCreate(): void {
        this.$i18n.loadNamespaces('clevehr')
    }

    public mounted() {
        this.dynamicRefresh = !(this.modeSimulation || this.printMode)
        if (!this.printMode) {
            this.resizeObserver = new ResizeObserver(this.updateMaxDataRowCanDisplayedOnTable)
            this.resizeObserver.observe(document.querySelector('#screenControlTableContainer')!)
        }

        this.$i18n.loadNamespaces(['screenControl'])
        if (this.modeSimulation) {
            const fakeOperation = ScreenControlFakeData.createFakeData()
            this.setAnesthetistFromOperationList(fakeOperation)
            this.groupOperations = this.groupOperationsByRooms(fakeOperation)
        } else {
            this.loadData()
        }
    }

    @Watch('printMode', { immediate: true })
    private watchPrintMode(printMode: boolean): void {
        this.printModeProvide = printMode
    }

    @Watch('fakeTemplate', { deep: true })
    private watchFakeTemplate(template: ScreenControlTemplateData): void {
        this.headers = []
        this.template = template
        if (template.dataTypes) {
            this.dataTypes = template.dataTypes
            this.template = {
                ...template,
                dataTypes: this.formatDataTypes,
            }
            this.headers = this.template?.dataTypes ? this.dataTypeToHeader(this.template.dataTypes) : []
        }
    }

    public get parsedDate(): Nullable<Date> {
        return this.date ? parse(this.date, this.DATE_PARSE_FORMAT, new Date()) : null
    }

    public get printPageHeigth(): string {
        return PAGE_PRINT_HEIGHT + 'vh'
    }

    public get printPadding(): string {
        return PRINT_MARGIN + 'px'
    }

    public extractRoomFromGroupOperationPrint(groupRoomOperation: Operation[][]) {
        const operations = groupRoomOperation[groupRoomOperation.length - 1]
        const operation = operations ? operations[operations.length - 1] : null
        return operation?.room?.name
    }

    private dataTypeToHeader(dataTypes: ScreenControlDataTypeData[]): ClvrScreenControlHeadersInterface[] {
        const headers: ClvrScreenControlHeadersInterface[] = []
        dataTypes
            ?.filter(dataType => dataType.enable && !(this.printMode && CELL_COMPONENTS_MAPPING[dataType.name]?.print))
            .forEach(dataType => {
                const combineColumn: Nullable<string> = mappingColumn(
                    dataType.name,
                    ClvrScreenControlHeadersEnumRefLabels,
                )
                const combinedColumn: ClvrScreenControlHeadersInterface | undefined = this.headers.find(
                    header => header.name === combineColumn,
                )
                // check if the combined column is already added in the headers it can only be added once
                if (combinedColumn) return
                if (dataType.id) {
                    headers.push(this.createHeader(dataType.id, combineColumn ?? dataType.name, dataType))
                }
            })

        return headers
    }

    public get screenControlName(): string {
        return this.screenControl?.name ?? ''
    }

    public get operatingRoomName(): string {
        return this.screenControl?.operatingRoom?.name ?? ''
    }

    public get orderedHeaders(): ClvrScreenControlHeadersInterface[] {
        return this.headers.sort(
            (header1, header2) => (header1.rank || Number.MAX_SAFE_INTEGER) - (header2.rank || Number.MAX_SAFE_INTEGER),
        )
    }

    public get aliasOption(): ScreenControlAliasOptionEnum {
        return this.template?.aliasOption || DEFAULT_ALIAS_OPTION
    }

    /**
     * LifeCycle. Fired before compile and DOM
     */
    public loadData(): void {
        // @TODO do not forget to change the back in a next mr to take into account the pagination and to send the params in the ajax request
        if (this.screenControlId) {
            this.loadingScTemplates = true
            this.screenControlStore
                .loadScreenControl(this.screenControlId)
                .then(result => {
                    this.screenControl = result.getOneData()
                    this.hideData = !this.screenControl?.isActive
                    if (this.screenControl) {
                        this.roomsId = this.screenControl.rooms.map(room => room.id).join(',')
                        this.screenControlStore
                            .loadScTemplateWithEnabledDataType(this.screenControl.templateId)
                            .then(result => {
                                this.template = result.getOneData()
                                if (this.template) {
                                    this.dataTypes = this.template.dataTypes || []
                                    this.template = {
                                        ...this.template,
                                        dataTypes: this.formatDataTypes,
                                    }

                                    this.refreshInterval =
                                        (this.template.refreshInterval || SC_DEFAULT_REFRESH_INTERVAL) * 1000
                                }
                                // creation of headers taking into account the headers combined or not example
                                // for the material column this column can combine 3 fields including (MATERIAL_LIST,MATERIAL_TO_PROVIDE,REMARKS)
                                this.headers = this.template?.dataTypes
                                    ? this.dataTypeToHeader(this.template.dataTypes)
                                    : []

                                if (this.screenControl?.isActive) {
                                    this.loadOperations(this.page, this.maxDataRowCanDisplayedOnTable).subscribe({
                                        complete: () => {
                                            if (!this.printMode) {
                                                this.loadOperationsWithRefresh()
                                            } else {
                                                this.loadingScTemplates = false
                                                this.$nextTick(function () {
                                                    window.print()
                                                })
                                            }
                                        },
                                    })
                                }
                            })
                    }
                })
                .finally(() => {
                    this.loadingScTemplates = false
                })
        }
    }

    private loadOperationsWithRefresh() {
        setInterval(() => {
            this.operations = []
            this.groupOperations = []
            this.loadOperations(this.page, this.maxDataRowCanDisplayedOnTable)
        }, this.refreshInterval)
    }

    private createHeader(
        id: number,
        name: string,
        dataType: ScreenControlDataTypeData,
    ): ClvrScreenControlHeadersInterface {
        return {
            id,
            label: this.$t(`screenControl:screenControl-${name}`),
            name,
            rank: dataType?.rank,
            align: 'start',
            sortable: false,
            width: ClvrScreenControlHeadersConfig[name]?.width || 'auto',
        }
    }

    private loadOperations(page: number, quantity: number): Observable<JsonApiResponseAdapter<Operation>> {
        if (this.roomsId && this.dataTypeForApi) {
            this.loadingScTemplates = true
            const operationObservable = this.screenControlStore.loadOperations({
                dataTypeNames: this.dataTypeForApi,
                roomIds: this.roomsId,
                page: this.printMode ? undefined : page,
                itemPerPage: this.printMode ? undefined : quantity,
                date: this.date,
                hideIntervention: this.template?.isDoneInterventionHidden ? 1 : 0,
                displayCancelledIntervention: this.template?.displayCancelledIntervention ? 1 : 0,
            })
            operationObservable.subscribe({
                next: result => {
                    this.totalOperation = result.getMeta().count ?? null
                    this.operations = result.getManyData() ?? []
                    this.setAnesthetistFromOperationList(this.operations)
                    this.groupOperations = this.groupOperationsByRooms(this.operations)

                    if (this.printMode) {
                        this.groupOperationsPrint = this.groupOperationsByPrintPage(this.groupOperations)
                        this.loadingScTemplates = false
                    }
                },
                complete: () => {
                    this.page =
                        this.totalOperation &&
                        (this.page + 1) * this.maxDataRowCanDisplayedOnTable < this.totalOperation
                            ? this.page + 1
                            : this.FIRST_PAGE
                    this.loadingScTemplates = false
                },
            })
            return operationObservable
        }
        return EMPTY
    }

    public get currentPage(): number {
        return this.page === this.FIRST_PAGE ? this.lastPage : this.page
    }

    public get lastPage(): number {
        return this.maxDataRowCanDisplayedOnTable > 0 && this.totalOperation
            ? Math.ceil(this.totalOperation / this.maxDataRowCanDisplayedOnTable)
            : this.FIRST_PAGE
    }

    private groupOperationsByRooms(operations: Operation[]): Operation[][] {
        const groupOperations: Operation[][] = []
        operations.forEach(operation => {
            const roomId = operation.room?.id ?? 0
            if (!groupOperations[roomId]) {
                groupOperations[roomId] = []
            }
            groupOperations[roomId].push(operation)
        })
        return groupOperations.sort((a, b) => {
            return (a[0]?.room?.name ?? '').localeCompare(b[0]?.room?.name ?? '')
        })
    }

    public setAnesthetistFromOperationList(operationList: Operation[]) {
        operationList.forEach(operation => {
            operation.setAnestesistListForScreenControl()
        })
    }

    public get formatDataTypes(): ScreenControlDataTypeData[] {
        return ClvrScreenControlDataTypeDataHandler.formatDataTypes(
            this.dataTypes,
            this.template?.isTrigramMode,
            this.template?.isDataAggregated,
        )
    }

    public get dataTypeForApi(): string {
        if (this.dataTypes) {
            return (
                ClvrScreenControlDataTypeDataHandler.getNameAndChildrenNameFromDataTypes(this.dataTypes).join(',') || ''
            )
        }
        return ''
    }

    public groupOperationsByPrintPage(roomGroupOperations: Operation[][]): Operation[][][] {
        const pages: Operation[][][] = []

        const availableSpace = PAGE_A4_HEIGHT - HEADER_SIZE - PRINT_MARGIN
        const maxPerPage = Math.floor(
            availableSpace / (SC_DEFAULT_HEIGHT_ROW + SC_DEFAULT_BORDER_ROW * 2 + SC_DEFAULT_ROW_SPACING),
        )
        let currentPage = 0
        let currentRoomId: Nullable<number> = null
        roomGroupOperations.forEach(operations => {
            operations.forEach(operation => {
                if (operation.room?.id) {
                    if (
                        (currentRoomId && operation.room?.id !== currentRoomId) ||
                        pages?.[currentPage]?.[operation.room.id].length >= maxPerPage
                    ) {
                        currentPage = currentPage + 1
                    }

                    currentRoomId = operation.room?.id

                    if (!pages[currentPage]) {
                        pages[currentPage] = []
                    }
                    if (!pages[currentPage][operation.room?.id]) {
                        pages[currentPage][operation.room?.id] = []
                    }

                    pages[currentPage][operation.room.id].push(operation)
                }
            })
        })
        return pages
    }

    public updateMaxDataRowCanDisplayedOnTable() {
        this.page = this.FIRST_PAGE
        this.maxDataRowCanDisplayedOnTable = this.getMaxDataRowCanDisplayedOnTable()
        this.loadOperations(this.page, this.maxDataRowCanDisplayedOnTable)
    }

    public getMaxDataRowCanDisplayedOnTable(): number {
        const refTable: Undefinable<ClvrScreenControlTable> = this.$refs?.screenControlTable as ClvrScreenControlTable
        const tbodyElementRef: HTMLElement = refTable.$refs.tbodyScreenControl as HTMLElement

        const tbodyCoor: DOMRect = tbodyElementRef.getBoundingClientRect()
        const windowBottom = window.innerHeight
        const pixelInterval = windowBottom - tbodyCoor.top

        const heightRow =
            Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0) > this.WIDE_SCREEN_BREAKPOINT
                ? SC_DEFAULT_HEIGHT_ROW * 2
                : SC_DEFAULT_HEIGHT_ROW

        return Math.floor(pixelInterval / (heightRow + SC_DEFAULT_BORDER_ROW * 2 + SC_DEFAULT_ROW_SPACING))
    }
}
